import {
  type AccentKey,
  type Appearance,
  type AppearancePatch,
  AppearanceSchema,
} from '@pm/schemas';
import {
  cn,
  Field,
  LabelCaps,
  MONO_FONTS,
  SANS_FONTS,
  Segmented,
  Select,
  StatusBadge,
  useTheme,
} from '@pm/ui';
import { useMutation } from '@tanstack/react-query';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

const ACCENTS: { key: AccentKey; label: string; color: string }[] = [
  { key: 'cyan', label: 'Ciano', color: '#22d3ee' },
  { key: 'violet', label: 'Violeta', color: '#8b5cf6' },
  { key: 'lime', label: 'Lime', color: '#a3e635' },
  { key: 'amber', label: 'Âmbar', color: '#fbbf24' },
  { key: 'rose', label: 'Rosa', color: '#f43f5e' },
  { key: 'blue', label: 'Azul', color: '#60a5fa' },
];

/** Configurações → Aparência (skill → tailwind-setup.md §6). Aplica na hora, salva com debounce. */
export function AppearancePage() {
  const { appearance, update } = useTheme();
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const timer = useRef<number | null>(null);

  const save = useMutation({
    mutationFn: (patch: AppearancePatch) => api.patch<Appearance>('/me/preferences', patch),
    onSuccess: () => setSavedAt(Date.now()),
  });

  function change(patch: AppearancePatch) {
    const next = update(patch);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => save.mutate(AppearanceSchema.parse(next)), 500);
  }

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  return (
    <div className="flex max-w-[960px] flex-col gap-gap-lg">
      <div className="flex items-center gap-gap">
        <div>
          <h1 className="text-xl font-semibold leading-tight">Aparência</h1>
          <p className="text-sm text-text-2">
            Tema, cor de destaque, densidade e fontes. Vale só para você.
          </p>
        </div>
        <span className="ml-auto font-mono text-xs text-text-3" aria-live="polite">
          {save.isPending
            ? 'Salvando…'
            : save.isError
              ? 'Falha ao salvar'
              : savedAt
                ? 'Preferências salvas'
                : ''}
        </span>
      </div>

      <div className="grid gap-gap-lg lg:grid-cols-[1fr_360px]">
        <div className="flex flex-col gap-gap-lg">
          <Section label="Tema">
            <Segmented
              ariaLabel="Tema"
              value={appearance.theme}
              onChange={(theme) => change({ theme })}
              options={[
                {
                  value: 'dark',
                  label: (
                    <>
                      <Moon /> Escuro
                    </>
                  ),
                },
                {
                  value: 'light',
                  label: (
                    <>
                      <Sun /> Claro
                    </>
                  ),
                },
                {
                  value: 'system',
                  label: (
                    <>
                      <Monitor /> Sistema
                    </>
                  ),
                },
              ]}
            />
          </Section>

          <Section label="Accent">
            <div role="radiogroup" aria-label="Cor de destaque" className="flex gap-2.5">
              {ACCENTS.map((a) => {
                const active = a.key === appearance.accent;
                return (
                  <button
                    key={a.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-label={a.label}
                    title={a.label}
                    onClick={() => change({ accent: a.key })}
                    style={{ background: a.color }}
                    className={cn(
                      'grid size-6 place-items-center rounded-full border-2 border-transparent transition-[box-shadow,border-color] duration-(--dur-fast) focus-visible:outline-none focus-visible:shadow-ring',
                      active && 'border-text-1 shadow-glow',
                    )}
                  >
                    {active ? (
                      <Check className="size-3.5 text-[#070b14]" strokeWidth={3} aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section label="Densidade">
            <Segmented
              ariaLabel="Densidade"
              value={appearance.density}
              onChange={(density) => change({ density })}
              options={[
                { value: 'compact', label: 'Compacta' },
                { value: 'comfortable', label: 'Confortável' },
              ]}
            />
          </Section>

          <Section label="Fontes">
            <div className="grid gap-gap-lg sm:grid-cols-2">
              <Field label="Padrão (sans)" htmlFor="font-sans">
                <Select
                  id="font-sans"
                  value={appearance.font_sans}
                  onChange={(e) => change({ font_sans: e.target.value as Appearance['font_sans'] })}
                >
                  {SANS_FONTS.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                      {f.condensed ? ' (condensada)' : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Mono"
                htmlFor="font-mono"
                hint={
                  MONO_FONTS.find((f) => f.key === appearance.font_mono)?.system
                    ? 'Fonte do macOS. No Windows aparece a Consolas e no Linux a DejaVu Sans Mono, de desenho equivalente.'
                    : undefined
                }
              >
                <Select
                  id="font-mono"
                  value={appearance.font_mono}
                  onChange={(e) => change({ font_mono: e.target.value as Appearance['font_mono'] })}
                >
                  {MONO_FONTS.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                      {f.system ? ' (sistema)' : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Section>
        </div>

        <aside className="flex flex-col gap-4 self-start rounded-md border border-border bg-surface-1 p-4">
          <LabelCaps>Prévia</LabelCaps>
          <div>
            <p className="text-lg font-medium leading-tight">
              Configurar pixel e conversões no site
            </p>
            <p className="mt-1 text-sm text-text-2">
              Instalar o pixel da Meta e configurar os eventos via GTM. Validar antes de liberar a
              campanha.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <StatusBadge status="IN_PROGRESS" />
            <StatusBadge status="PENDING_APPROVAL" />
            <StatusBadge status="BLOCKED" />
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 font-mono text-sm">
            <dt className="text-text-3">ID</dt>
            <dd>#TSK-1042</dd>
            <dt className="text-text-3">Prazo</dt>
            <dd className="text-danger">15 set 2026 · 18:00</dd>
            <dt className="text-text-3">Timer</dt>
            <dd>01:42:07</dd>
            <dt className="text-text-3">Custo</dt>
            <dd>R$ 1.200,00</dd>
          </dl>
          <pre className="rounded-sm bg-surface-2 p-2.5 font-mono text-xs text-text-2">{`{ "status": "IN_PROGRESS", "priority": "URGENT" }`}</pre>
        </aside>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5 rounded-md border border-border bg-surface-1 p-4">
      <LabelCaps>{label}</LabelCaps>
      {children}
    </section>
  );
}
